import { useEffect, useMemo, useRef } from 'react'
import { useUiStrings } from '@/hooks/useUiStrings'
import useAutoScroll from '@/hooks/useAutoScroll'
import { ASSIST_SUGGESTIONS_ONLY } from '@/constants/feature-flags'
import { fmt } from '@/constants/ui-locale'
import type {
  AssistAskError,
  AssistAskInput,
  AssistCard,
  NestedAssistAnswer,
  QuerySuggestionCard,
} from './assist.types'
import {
  resolveAssistSourceTag,
  selectCopilotItems,
  stableCardList,
  toAssistDisplayRows,
} from './assist.types'
import { answerCardToSuggestion } from './assistAnswerAdapter'
import { resolveBacklogRefusal } from './assistBacklogReason'
import AssistBacklogTicketCard from './AssistBacklogTicketCard'
import AssistConflictWarningCard from './AssistConflictWarningCard'
import AssistSuggestionChips from './AssistSuggestionChips'
import QuestionCard from './QuestionCard'

type CopilotContentProps = {
  cards: AssistCard[]
  askError: AssistAskError | null
  isAsking: boolean
  onAsk: (input: AssistAskInput) => void
  expandedCardIds: string[]
  onToggleAnswer: (rowKey: string) => void
  expandedQuoteIds?: string[]
  onToggleQuote?: (quoteKey: string) => void
}

function resolveAskErrorLabel(askError: AssistAskError | null, t: ReturnType<typeof useUiStrings>['t']) {
  if (!askError) return null
  // Checked first: a Backlog refusal reaches the panel with the same
  // `not_supported` code as "the answer flow is switched off", and the line
  // below would answer all six of them with the wrong one.
  const refusal = resolveBacklogRefusal(askError, t)
  if (refusal.message) return refusal.message
  if (askError.code === 'session_not_ready') return t.assistAskErrorSessionNotReady
  if (askError.code === 'not_supported') return t.assistAskErrorNotSupported
  const serverMessage = askError.message?.trim()
  if (serverMessage) return serverMessage
  return t.assistAskErrorBadRequest
}

const SECTION_HEADING =
  'text-[11px] font-bold uppercase leading-[16.5px] tracking-[0.55px] text-slate-500 dark:text-[var(--text-tertiary)]'

/** A suggestion list row: rendered inside the highlighted speaker card. */
type SuggestionRow = { card: QuerySuggestionCard; index: number }

/** Any other card row: rendered in the ordered list below the speaker card. */
type OtherRow = { card: Exclude<AssistCard, QuerySuggestionCard>; index: number }

type SuggestionListProps = {
  rows: SuggestionRow[]
  answersByQuestion: Map<string, NestedAssistAnswer>
  expandedCardIds: string[]
  disabled: boolean
  onAsk: (input: AssistAskInput) => void
  onToggleAnswer: (rowKey: string) => void
  expandedQuoteIds?: string[]
  onToggleQuote: (quoteKey: string) => void
}

function SuggestionList({
  rows,
  answersByQuestion,
  expandedCardIds,
  disabled,
  onAsk,
  onToggleAnswer,
  expandedQuoteIds,
  onToggleQuote,
}: SuggestionListProps) {
  if (rows.length === 0) return null

  return (
    <div className="flex flex-col gap-1.5">
      {rows.map(({ card, index }) => (
        <AssistSuggestionChips
          key={`${card.cardId}-${index}`}
          card={card}
          answersByQuestion={answersByQuestion}
          expandedCardIds={expandedCardIds}
          disabled={disabled}
          onAsk={onAsk}
          onToggleAnswer={onToggleAnswer}
          expandedQuoteIds={expandedQuoteIds}
          onToggleQuote={onToggleQuote}
        />
      ))}
    </div>
  )
}

/**
 * One row of the reader's own column: an answer they asked for, or a ticket or
 * conflict the meeting turned up.
 *
 * Pulled out of the JSX because that column moved above the suggestions
 * (BRAI-662) and a `switch` nested two levels into a conditional is harder to
 * move than to read.
 *
 * @param expandedCardIds Row keys the reader has opened; a pending answer is
 *   open regardless, so its loading state stays visible.
 */
function AssistListRow({
  card,
  rowKey,
  expandedCardIds,
  onToggleAnswer,
  t,
}: {
  card: OtherRow['card']
  rowKey: string
  expandedCardIds: string[]
  onToggleAnswer: (rowKey: string) => void
  t: ReturnType<typeof useUiStrings>['t']
}) {
  switch (card.type) {
    case 'answer': {
      const suggestion = answerCardToSuggestion(card, rowKey, t)
      const tagPill = resolveAssistSourceTag(card.route, card.sourceType)
      const tagLabel = tagPill.kind === 'locale' ? t[tagPill.labelKey] : tagPill.label
      const expanded = card.status === 'pending' || expandedCardIds.includes(rowKey)
      return (
        <li>
          <QuestionCard
            suggestion={suggestion}
            expanded={expanded}
            tagLabel={tagLabel}
            tagTone={tagPill.tone}
            onToggle={() => onToggleAnswer(rowKey)}
          />
          {card.status === 'pending' ? (
            <p className="sr-only">{fmt(t.assistAnswerPending, { question: card.question })}</p>
          ) : null}
        </li>
      )
    }
    case 'backlog_ticket':
      return (
        <li>
          <AssistBacklogTicketCard card={card} />
        </li>
      )
    case 'conflict_warning':
      return (
        <li>
          <AssistConflictWarningCard card={card} />
        </li>
      )
  }
}

export default function CopilotContent({
  cards,
  askError,
  isAsking,
  onAsk,
  expandedCardIds,
  onToggleAnswer,
  expandedQuoteIds,
  onToggleQuote = () => undefined,
}: CopilotContentProps) {
  const { t } = useUiStrings()
  const errorLabel = resolveAskErrorLabel(askError, t)
  // Every question the AI raised is an item, and it states the turn it came from
  // as soon as it arrives — the answer only adds the AI content underneath it.
  const items = useMemo(() => selectCopilotItems(cards), [cards])
  const rows = toAssistDisplayRows(items)

  const suggestionRows: SuggestionRow[] = []
  const otherRows: OtherRow[] = []
  for (const row of rows) {
    const { card, index } = row
    if (card.type === 'query_suggestion') {
      suggestionRows.push({ card, index })
    } else {
      otherRows.push({ card, index })
    }
  }


  // Matching answers expand inside their suggestion; typed answers stay in the list.
  const suggestionTexts = new Set(
    suggestionRows.flatMap(({ card }) => card.suggestions.map((suggestion) => suggestion.text))
  )
  const answersByQuestion = new Map<string, NestedAssistAnswer>()
  for (const { card, index } of otherRows) {
    if (card.type !== 'answer' || !suggestionTexts.has(card.question)) continue
    answersByQuestion.set(card.question, { card, rowKey: `${card.cardId}-${index}` })
  }
  const listRows = otherRows.filter(
    ({ card }) => card.type !== 'answer' || !suggestionTexts.has(card.question)
  )

  // Empty means no visible suggestions or non-suggestion cards.
  const visibleListRows = ASSIST_SUGGESTIONS_ONLY ? [] : listRows

  // Only what the lower half draws follows the live edge: the suggestion cards,
  // and the answers that expand inside them, which is where that half grows.
  // The reader's own questions are drawn above it (BRAI-662) and followed
  // separately just below — giving the hook both is what sent a question the
  // reader had only just typed scrolling past every suggestion to the far end
  // of the panel, the one place it was not.
  //
  // Held stable while those cards do not change: the hook follows array
  // identity, so a list rebuilt on every render would have it read a question
  // added at the top as new content arriving at the bottom.
  const listedCardIds = new Set(visibleListRows.map(({ card }) => card.cardId))
  const followedItemsRef = useRef<AssistCard[]>([])
  // Streamed card updates replace the array identity, which drives auto-follow.
  followedItemsRef.current = stableCardList(
    rows.filter(({ card }) => !listedCardIds.has(card.cardId)).map(({ card }) => card),
    followedItemsRef.current
  )
  const { ref: scrollRef } = useAutoScroll(followedItemsRef.current)

  const questionListRef = useRef<HTMLUListElement>(null)
  // Null until the first commit, so a panel opened on questions that were
  // already there leaves the reader where it put them. Only a list that grows
  // afterwards is somebody having pressed send.
  const questionCountRef = useRef<number | null>(null)
  useEffect(() => {
    const count = visibleListRows.length
    const previous = questionCountRef.current
    questionCountRef.current = count
    if (previous === null || count <= previous) return
    const newest = questionListRef.current?.lastElementChild
    const box = scrollRef.current
    if (!(newest instanceof HTMLElement) || !box) return
    // Scrolled here rather than by `scrollIntoView`, which walks up and scrolls
    // every scroll container between the element and the document — and an
    // `overflow-hidden` box is one of them, since it has a scrollport the user
    // merely cannot reach. The meeting layout wraps its row of panes in two,
    // so putting this question at the top of the panel dragged the panes up
    // with it and left a white band underneath that nothing could put back:
    // nobody can scroll an `overflow-hidden` box by hand.
    //
    // Aligned to the top of the panel, which is what `block: 'start'` was for:
    // the answer streams in underneath the question, and a question pulled only
    // just into view would have the thing the reader is waiting for arrive
    // off-screen. Measured through `getBoundingClientRect` rather than
    // `offsetTop`, which is relative to whichever ancestor happens to be
    // positioned rather than to this box.
    const top = box.scrollTop + newest.getBoundingClientRect().top - box.getBoundingClientRect().top
    // The scroll events this emits reach `useAutoScroll` with no programmatic
    // flag set, so it reads them as the reader scrolling up and stops following
    // the live edge. That is the behaviour wanted: having been brought to their
    // own question, a reader should not then be towed away by the next
    // suggestion the meeting produces.
    box.scrollTo({ top, behavior: 'smooth' })
    // `scrollRef` is listed because it arrives from a custom hook, so the lint
    // rule cannot see that it is a ref and therefore stable. It never changes
    // identity, so naming it costs nothing and keeps the rule honest.
  }, [visibleListRows.length, scrollRef])

  const suggestionCount = suggestionRows.reduce(
    (total, { card }) => total + card.suggestions.length,
    0
  )
  // The error already explains why the panel is empty.
  const isEmpty = !errorLabel && suggestionCount === 0 && visibleListRows.length === 0

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-[#eef1fd] px-3 py-3 dark:bg-[var(--bg-chat)]"
      >
        {/* Above the suggestions, not below them (BRAI-662).

            Both blocks grow, and a browser does not hold a reading position
            when content is added above it. With the suggestion list on top,
            every new suggestion the meeting produced shoved the reader's own
            question further down, until the answer they were waiting for was
            off-screen and they had no way of knowing where it went.
            Underneath, the suggestions grow away from it instead and the
            conversation the reader started stays exactly where they left it. */}
        {visibleListRows.length > 0 ? (
          <>
            <h2 className={SECTION_HEADING}>{t.copilotYourQuestionsTitle}</h2>
            <ul ref={questionListRef} className="space-y-3">
              {visibleListRows.map(({ card, index }) => (
                <AssistListRow
                  key={`${card.cardId}-${index}`}
                  card={card}
                  rowKey={`${card.cardId}-${index}`}
                  expandedCardIds={expandedCardIds}
                  onToggleAnswer={onToggleAnswer}
                  t={t}
                />
              ))}
            </ul>
          </>
        ) : null}

        <h2 className={SECTION_HEADING}>{t.copilotSuggestionsTitle}</h2>

        <SuggestionList
          rows={suggestionRows}
          answersByQuestion={answersByQuestion}
          expandedCardIds={expandedCardIds}
          disabled={isAsking}
          onAsk={onAsk}
          onToggleAnswer={onToggleAnswer}
          expandedQuoteIds={expandedQuoteIds}
          onToggleQuote={onToggleQuote}
        />

        {errorLabel ? (
          <p
            className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400"
            role="alert"
          >
            {errorLabel}
          </p>
        ) : null}

        {isEmpty ? (
          <p className="text-xs text-slate-500 dark:text-[var(--text-tertiary)]">{t.copilotEmpty}</p>
        ) : null}

      </div>
    </div>
  )
}
